import { useState } from 'react';
import { X, Coffee, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { BreakRequestType } from '../../types';

const BREAK_OPTIONS: { type: BreakRequestType; label: string; description: string }[] = [
  { type: 'lunch', label: 'Lunch', description: 'Meal break' },
  { type: 'rest', label: 'Rest', description: 'Short rest period' },
  { type: 'personal', label: 'Personal', description: 'Personal time' },
  { type: 'other', label: 'Other', description: 'Custom break type' },
];

interface MdtBreakRequestModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (type: BreakRequestType, notes?: string) => Promise<void>;
  submitting?: boolean;
}

export default function MdtBreakRequestModal({
  open,
  onClose,
  onSubmit,
  submitting = false,
}: MdtBreakRequestModalProps) {
  const [selected, setSelected] = useState<BreakRequestType | null>(null);
  const [notesText, setNotesText] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!selected) {
      setError('Select a break type');
      return;
    }
    if (selected === 'other' && !notesText.trim()) {
      setError('Please describe your break');
      return;
    }
    setError(null);
    try {
      await onSubmit(selected, notesText.trim() || undefined);
      setSelected(null);
      setNotesText('');
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to submit break request');
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setSelected(null);
    setNotesText('');
    setError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div
        className="w-full max-w-md rounded-t-2xl p-5 space-y-4 animate-in slide-in-from-bottom-4 duration-200"
        style={{ background: '#1a1d23', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Coffee className="w-5 h-5 text-purple-400" />
            <p className="text-[15px] font-extrabold text-white">Request Break</p>
          </div>
          <button onClick={handleClose} disabled={submitting}>
            <X className="w-4 h-4 text-white/40" />
          </button>
        </div>

        <p className="text-[11px] text-white/40">
          Select a break type. Command will be notified and must approve before your status changes.
        </p>

        <div className="grid grid-cols-2 gap-2">
          {BREAK_OPTIONS.map((opt) => (
            <button
              key={opt.type}
              type="button"
              onClick={() => {
                setSelected(opt.type);
                setError(null);
              }}
              className={cn(
                'p-3 rounded-xl text-left transition-all border',
                selected === opt.type
                  ? 'border-purple-400/40 bg-purple-400/10'
                  : 'border-white/[0.06] bg-white/[0.02] hover:border-white/10'
              )}
            >
              <div className={cn('text-sm font-bold', selected === opt.type ? 'text-purple-300' : 'text-white/90')}>
                {opt.label}
              </div>
              <div className="text-[10px] text-white/35 mt-0.5">{opt.description}</div>
            </button>
          ))}
        </div>

        <div>
          <label className="text-[10px] font-bold uppercase tracking-wide text-white/30">Notes (optional)</label>
          <textarea
            rows={2}
            placeholder={selected === 'other' ? 'Required for Other — describe your break...' : 'Any details for Command...'}
            value={notesText}
            onChange={(e) => setNotesText(e.target.value)}
            className="mt-1 w-full px-4 py-3 rounded-xl text-sm text-white/90 placeholder-white/25 outline-none resize-none"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
          />
        </div>

        {error && <p className="text-xs text-red-400">{error}</p>}

        <button
          disabled={submitting || !selected}
          onClick={handleSubmit}
          className={cn(
            'w-full py-3 rounded-xl text-sm font-bold transition-all active:scale-95 flex items-center justify-center gap-2',
            submitting || !selected ? 'opacity-50 cursor-not-allowed' : ''
          )}
          style={{ background: 'rgba(184,140,255,0.2)', border: '1px solid rgba(184,140,255,0.35)', color: '#c084fc' }}
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {submitting ? 'Submitting...' : 'Submit Request'}
        </button>

        <button
          onClick={handleClose}
          disabled={submitting}
          className="w-full py-2.5 rounded-xl text-xs font-medium text-white/30"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
